import GameModel from "../../GameModel";
import Projection from "../../Projection";
import { Settings } from "../../Settings";
import PartyTarget from "../character/PartyTarget";
import Crew from "../crew/Crew";
import GridInversionOfControl from "../grid/GridInversionOfControl";
import GridRegion from "../grid/GridRegion";
import GridTile from "../grid/GridTile";
import { TileBorderCode } from "../grid/TileBorderCode";
import { TileTypeRarity } from "../grid/TileTypeRarity";
import VectorField from "../grid/VectorField";
import WorldGrid from "../grid/WorldGrid";
import Vector from "../math/Vector";
import PerformanceMetrics from "../performance/PerformanceMetrics";
import { VectorFieldScores } from "./VectorFieldScores";

export default class VectorFieldGenerator extends GameModel {
    /**
     * @param {WorldGrid} worldGrid
     * @param {Projection} projection
     * @param {VectorField} vectorField 
     * @param {PartyTarget} partyTarget
     * @param {Crew} crew
     */
    constructor(worldGrid, projection, vectorField, partyTarget, crew) {
        super();

        /**
         * @constant
         * @type {WorldGrid}
         */
        this.worldGrid = worldGrid;

        /**
         * @constant
         * @type {Projection}
         */
        this.projection = projection;

        /**
         * @constant
         * @type {VectorField}
         */
        this.vectorField = vectorField;

        /**
         * @constant
         * @type {PartyTarget}
         */
        this.partyTarget = partyTarget;

        /**
         * @constant
         * @type {Crew}
         */
        this.crew = crew;


        /**
         * @private
         * @type {Vector}
         */
        this.startTileOrigin = null;
        /**
         * @private
         * @type {number}
         */
        this.vectorFieldNumber = 0;

        const thisRef = this;

        /**
         * @constant
         * @private
         * @type {Function}
         */
        this.minePremiumTileRegionCallback = 
            /**
             * @param {GridRegion} region 
             */
            function(region) {
                const mineTiles = region.mineTiles;
                region.vectorFieldNumber = thisRef.vectorField.vectorFieldNumber;

                let count = 0;
                for (let i = 0; i < mineTiles.length; i++) {
                    const tile = mineTiles[i];
                    if (!tile || tile.open) {
                        continue;
                    }

                    if (tile.isCurrency()) {
                        thisRef.vectorField.currencyMiningCandidates.push(tile);
                        count++;
                    }
                    else {   // GOLD, RUBY, UPGRADE
                        thisRef.vectorField.priorityMiningCandidates.push(tile);
                        count++;
                    }

                    // Give the tile a mine score.
                    tile.pathData.setMineScore(VectorFieldScores.PREMIUM, thisRef.vectorField.vectorFieldNumber);
                }
            };

        /**
         * @constant
         * @private
         * @type {Function}
         */
        this.commonTileRegionCallback = 
            /**
             * @param {GridRegion} region 
             */
            function(region) {
                const tiles = region.tiles;
                for (let i = 0; i < tiles.length; i++) {
                    /**
                     * @type {GridTile}
                     */
                    const tile = tiles[i];

                    // Tiles can only be mining candidates if they are closed and have been seen.
                    if (!tile || tile.tileType.rarityModifier !== TileTypeRarity.COMMON) {
                        continue;
                    }

                    if (!tile.lighting.everVisibleToCharacter) {
                        continue;
                    }
                    if (tile.open) {
                        continue;
                    }
                    
                    const mineScore = thisRef.calculateCommonTileMineScore(tile);
                    tile.pathData.setMineScore(mineScore, thisRef.vectorFieldNumber);

                    // Common tiles with a mine score of 0 are not candidates for mining.
                    if (mineScore > VectorFieldScores.WORTHLESS) {
                        thisRef.vectorField.commonMiningCandidates.push(tile);
                    }
                }
            };
    }

    /**
     * @param {GridTile} startTile
     * @param {boolean} ignoreCommonTiles
     * @param {boolean} ignorePremiumTiles
     * @param {boolean} smallGridMode
     * @param {number} destinationDirection 0=center, 1=down, 2=up, 3=right
     */
    generateVectorField(startTile, ignoreCommonTiles, ignorePremiumTiles, smallGridMode, destinationDirection) {

        // The tile can be null right when we load up a save.
        if (!startTile) {
            console.log('VectorFieldGenerator - not generating: no tile');
            return;
        }

        // Don't create a vector field if the grid is not loaded. This should never happen
        const grid = startTile.getGrid();
        if (!grid.isLoaded()) {
            console.log('VectorFieldGenerator - not generating: grid not loaded?');
            return;
        }

        PerformanceMetrics.startTime('FindTargets');

        this.startTileOrigin = startTile.origin;

        // Clear the queues
        this.vectorField.commonMiningCandidates.length = 0;
        this.vectorField.currencyMiningCandidates.length = 0;
        this.vectorField.priorityMiningCandidates.length = 0;

        this.vectorField.ignoreCommonTiles = ignoreCommonTiles;
        this.vectorField.ignorePremiumTiles = ignorePremiumTiles;
        
        ////////this.vectorField.destinationTileCandidates.length = 0;
        this.vectorField.destinationTileCandidates.fill(null);

        // Increment the vector field number
        this.vectorField.vectorFieldNumber++;
        this.vectorField.centerTile = startTile;

        // Get some info from the vector field.
        this.vectorFieldNumber = this.vectorField.vectorFieldNumber;

        // The mine tile radius is larger than the common tile radius.
        ///////if (!ignorePremiumTiles) {
            const premiumPixelRadius = (smallGridMode ? 12 : 25) * Settings.tile.size;
            GridInversionOfControl.allNearbyRegions(this.worldGrid, this.projection, this.startTileOrigin, premiumPixelRadius, this.minePremiumTileRegionCallback);
        //////////}

        // Maybe don't add common tiles if there are enough good tiles to mine
        const numGoodTiles = this.vectorField.currencyMiningCandidates.length + this.vectorField.priorityMiningCandidates.length;

        if (!ignoreCommonTiles && numGoodTiles < this.crew.miners.length) {
            const commonPixelRadius = (smallGridMode ? 6 : 15) * Settings.tile.size;
            GridInversionOfControl.allNearbyRegions(this.worldGrid, this.projection, this.startTileOrigin, commonPixelRadius, this.commonTileRegionCallback);
        }

        // If there are no mine targets, then we should add in some destination tiles.
        //if (ignorePremiumTiles || this.vectorField.isVectorFieldMineTargetsEmpty()) {
            this.addDestinationTiles(startTile, destinationDirection, smallGridMode);
        //}

        PerformanceMetrics.endTime('FindTargets');
    }

    /**
     * @private
     * @param {GridTile} tile 
     * @param {number} destinationDirection 1=down, 2=up, 3=right
     * @param {boolean} smallGridMode
     */
    addDestinationTiles(tile, destinationDirection, smallGridMode) {
        if (!tile) {
            return;
        }

        // if (destinationDirection === 0) {
        //     this.addCenterDestinationTiles(tile);
        //     return;
        // }

        if (destinationDirection === 1) {
            this.addDownDestinationTiles(tile, smallGridMode);
        }
        else if (destinationDirection === 2) {
            this.addUpDestinationTiles(tile, smallGridMode);
        }
        else if (destinationDirection === 3) {
            this.addRightDestinationTiles(tile, smallGridMode);
        }
    }

    /**
     * @private
     * @param {GridTile} tile 
     * @param {boolean} smallGridMode
     */
    addDownDestinationTiles(tile, smallGridMode) {
        const tiles = this.vectorField.destinationTileCandidates;

        let yShiftPixels = Settings.tile.size * (smallGridMode ? 15 : 25);

        let count = 0;

        // 
        let downCenter = this.worldGrid.findGridTileXY(tile.origin.x, tile.origin.y + yShiftPixels);
        
        // The shifted tile can easily be off the side of the world. 
        // Reduce the shift until it is visible
        while (!downCenter && yShiftPixels > 0) {
            yShiftPixels -= Settings.tile.size * Settings.grid.numTileColsInRegion;
            downCenter = this.worldGrid.findGridTileXY(tile.origin.x, tile.origin.y + yShiftPixels);
        }

        if (downCenter) {
            if (tiles.length <= count) {
                tiles.push(downCenter);
            }
            else {
                tiles[count] = downCenter;
            }
            count++;
        }

        for (let i = 0; i < 15; i++) {
            const xShift = (i + 1) * Settings.tile.size;
            const downLeft = this.worldGrid.findGridTileXY(tile.origin.x - xShift, tile.origin.y + yShiftPixels);
            if (downLeft) {
                if (tiles.length <= count) {
                    tiles.push(downLeft);
                }
                else {
                    tiles[count] = downLeft;
                }
                count++;
            }
        }

        for (let i = 0; i < 15; i++) {
            const xShift = (i + 1) * Settings.tile.size;
            const downRight = this.worldGrid.findGridTileXY(tile.origin.x + xShift, tile.origin.y + yShiftPixels);
            if (downRight) {
                if (tiles.length <= count) {
                    tiles.push(downRight);
                }
                else {
                    tiles[count] = downRight;
                }
                count++;
            }
        }
    }

    /**
     * @private
     * @param {GridTile} tile 
     */
    addUpDestinationTiles(tile, smallGridMode) {
        const tiles = this.vectorField.destinationTileCandidates;
        
        let yShiftPixels = Settings.tile.size * (smallGridMode ? 15 : 25);

        let count = 0;

        let upCenter = this.worldGrid.findGridTileXY(tile.origin.x, tile.origin.y - yShiftPixels);

        // The shifted tile can easily be off the side of the world. 
        // Reduce the shift until it is visible
        while (!upCenter && yShiftPixels > 0) {
            yShiftPixels -= Settings.tile.size * Settings.grid.numTileColsInRegion;
            upCenter = this.worldGrid.findGridTileXY(tile.origin.x, tile.origin.y - yShiftPixels);
        }

        if (upCenter) {
            ////tiles.push(upCenter);
            if (tiles.length <= count) {
                tiles.push(upCenter);
            }
            else {
                tiles[count] = upCenter;
            }
            count++;
        }

        for (let i = 0; i < 15; i++) {
            const xShift = (i + 1) * Settings.tile.size;
            const upLeft = this.worldGrid.findGridTileXY(tile.origin.x - xShift, tile.origin.y - yShiftPixels);
            if (upLeft) {
                // tiles.push(upLeft);
                if (tiles.length <= count) {
                    tiles.push(upLeft);
                }
                else {
                    tiles[count] = upLeft;
                }
                count++;
            }
        }

        for (let i = 0; i < 15; i++) {
            const xShift = (i + 1) * Settings.tile.size;
            const upRight = this.worldGrid.findGridTileXY(tile.origin.x + xShift, tile.origin.y - yShiftPixels);
            if (upRight) {
                // tiles.push(upRight);
                if (tiles.length <= count) {
                    tiles.push(upRight);
                }
                else {
                    tiles[count] = upRight;
                }
                count++;
            }
        }
    }

    /**
     * @private
     * @param {GridTile} tile 
     */
    addRightDestinationTiles(tile, smallGridMode) {
        const tiles = this.vectorField.destinationTileCandidates;

        let xShiftPixels = Settings.tile.size * 25;

        let count = 0;

        let rightCenter = this.worldGrid.findGridTileXY(tile.origin.x + xShiftPixels, tile.origin.y);

        // The shifted tile can easily be off the side of the world. 
        // Reduce the shift until it is visible
        while (!rightCenter && xShiftPixels > 0) {
            xShiftPixels -= Settings.tile.size * Settings.grid.numTileColsInRegion;
            rightCenter = this.worldGrid.findGridTileXY(tile.origin.x + xShiftPixels, tile.origin.y);
        }

        if (rightCenter) {
            //tiles.push(rightCenter);
            if (tiles.length <= count) {
                tiles.push(rightCenter);
            }
            else {
                tiles[count] = rightCenter;
            }
            count++;
        }

        for (let i = 0; i < 15; i++) {
            const yShift = (i + 1) * Settings.tile.size;
            const rightUp = this.worldGrid.findGridTileXY(tile.origin.x + xShiftPixels, tile.origin.y - yShift);
            if (rightUp) {
                // tiles.push(rightUp);
                if (tiles.length <= count) {
                    tiles.push(rightUp);
                }
                else {
                    tiles[count] = rightUp;
                }
                count++;
            }
        }

        for (let i = 0; i < 15; i++) {
            const yShift = (i + 1) * Settings.tile.size;
            const rightDown = this.worldGrid.findGridTileXY(tile.origin.x + xShiftPixels, tile.origin.y + yShift);
            if (rightDown) {
                // tiles.push(rightDown);
                if (tiles.length <= count) {
                    tiles.push(rightDown);
                }
                else {
                    tiles[count] = rightDown;
                }
                count++;        
            }
        }
    }

    /**
     * @private
     * @param {GridTile} tile 
     */
    addCenterDestinationTiles(tile) {
        const tiles = this.vectorField.destinationTileCandidates;

        tiles.push(tile);

        let neighbors = tile.neighbors; // does not include diagonal neighbors
        for (let i = 0; i < neighbors.length; i++) {
            const neighbor = neighbors[i];
            if (neighbor) {
                tiles.push(neighbor);
            }
        }

        // add the diagonal neighbors
        const topLeft = tile.getNeighborTopLeft();
        const topRight = tile.getNeighborTopRight();
        const bottomLeft = tile.getNeighborBottomLeft();
        const bottomRight = tile.getNeighborBottomRight();

        if (topLeft) {
            tiles.push(topLeft);
        }
        if (topRight) {
            tiles.push(topRight);
        }
        if (bottomLeft) {
            tiles.push(bottomLeft);
        }
        if (bottomRight) {
            tiles.push(bottomRight);
        }
    }

    /**
     * @private
     * @param {GridTile} tile 
     * @returns {number}
     */
    calculateCommonTileMineScore(tile) {
        // Common tiles that do not expose anything by being mined are worthless.
        if (tile.borderCode === TileBorderCode.ALL_BORDERS_OPEN) {
            return VectorFieldScores.WORTHLESS;
        }

        // The value of a common tile depends completely on its neighbors:
        // 1. Mining the tile would reveal an unseen tile
        // 2. Mining the tile would reveal a cavern
        return this.calculateNeighborTileScore(tile);
    }

    /**
     * Calculate the mine score of a common tile based on that state of its neighbors.
     * @private
     * @param {GridTile} tile
     * @returns {number}
     */
    calculateNeighborTileScore(tile) {

        let hiddenNeighbor = false;
        let revealCavern = false;

        const neighbors = tile.neighbors;

        for (let i = 0; i < neighbors.length; i++) {
            const neighbor = neighbors[i];
            if (!neighbor) {
                continue;
            }

            // If the neighbor is known, then it provides no bonus to this tile.
            // If the tile does not support lighting, then it is SKY or GRASS and is always fully sunlit.
            if (neighbor.lighting.everVisibleToCharacter || !neighbor.tileType.lightingSupported) {
                continue;
            }

            // Unseen neighbor tiles that are open are caverns.
            if (neighbor.open) {
                revealCavern = true;
            }
            else {
                hiddenNeighbor = true;
            }
        }
        
        let score = 0;

        if (hiddenNeighbor) {
            score += VectorFieldScores.COMMON_HIDDEN_NEIGHBOR;
        }
        if (revealCavern) {
            score += VectorFieldScores.COMMON_REVEAL_CAVERN;
        }

        return score;
    }

    ////////////////////////////////////
    // GameModel
    ////////////////////////////////////

    /**
     * @override
     */
    resetFull() {
    }

    /**
     * @override
     */
    resetForPrestige() {
    }

}